import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CheckCircleRounded, ErrorRounded, InfoRounded, WarningRounded, Close } from '@mui/icons-material';

const ToastContext = createContext(null);

const SEVERITY_STYLES = {
    success: { bg: '#10b981', Icon: CheckCircleRounded },
    error: { bg: '#ef4444', Icon: ErrorRounded },
    warning: { bg: '#f59e0b', Icon: WarningRounded },
    info: { bg: '#3b82f6', Icon: InfoRounded }
};

const DEFAULT_DURATION = 3500;

export const ToastProvider = ({ children }) => {
    const [toast, setToast] = useState(null);
    const [visible, setVisible] = useState(false);
    const timerRef = useRef(null);
    const seqRef = useRef(0);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const dismiss = useCallback(() => {
        clearTimer();
        setVisible(false);
    }, []);

    const showToast = useCallback((message, options = {}) => {
        if (!message) return;
        clearTimer();
        const id = ++seqRef.current;
        const next = {
            id,
            message,
            severity: options.severity || 'info',
            duration: options.duration ?? DEFAULT_DURATION
        };
        setVisible(false);
        timerRef.current = setTimeout(() => {
            setToast(next);
            requestAnimationFrame(() => {
                setVisible(true);
                if (next.duration > 0) {
                    timerRef.current = setTimeout(() => setVisible(false), next.duration);
                }
            });
        }, 240);
    }, []);

    const updateToastMessage = useCallback((message) => {
        if (!message) return;
        setToast((prev) => (prev ? { ...prev, message } : prev));
    }, []);

    const api = useMemo(() => ({
        showToast,
        showSuccess: (msg, opts) => showToast(msg, { ...opts, severity: 'success' }),
        showErrorToast: (msg, opts) => showToast(msg, { ...opts, severity: 'error' }),
        showWarningToast: (msg, opts) => showToast(msg, { ...opts, severity: 'warning' }),
        showInfoToast: (msg, opts) => showToast(msg, { ...opts, severity: 'info' }),
        updateToastMessage,
        dismissToast: dismiss
    }), [showToast, updateToastMessage, dismiss]);

    useEffect(() => () => clearTimer(), []);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <ToastPortal toast={toast} visible={visible} onClose={dismiss} />
        </ToastContext.Provider>
    );
};

const ToastPortal = ({ toast, visible, onClose }) => {
    if (typeof document === 'undefined') return null;
    const style = SEVERITY_STYLES[toast?.severity] || SEVERITY_STYLES.info;
    const Icon = style.Icon;
    const progressKey = toast ? `${toast.id}-${toast.duration}` : 'none';

    return ReactDOM.createPortal(
        <div
            aria-live="polite"
            style={{
                position: 'fixed',
                top: 24,
                left: '50%',
                transform: `translate(-50%, ${visible ? '0' : '-24px'})`,
                opacity: visible ? 1 : 0,
                pointerEvents: 'none',
                transition: 'opacity 220ms ease, transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                zIndex: 2147483647,
                maxWidth: 'calc(100vw - 32px)'
            }}
        >
            <style>{`
                @keyframes toastProgressShrink {
                    from { transform: scaleX(1); }
                    to { transform: scaleX(0); }
                }
            `}</style>
            {toast && (
                <div
                    role="status"
                    style={{
                        position: 'relative',
                        pointerEvents: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        minWidth: 280,
                        maxWidth: 520,
                        padding: '14px 18px',
                        background: style.bg,
                        color: '#ffffff',
                        borderRadius: 12,
                        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.22), 0 2px 6px rgba(15, 23, 42, 0.08)',
                        fontFamily: 'inherit',
                        overflow: 'hidden'
                    }}
                >
                    <Icon style={{ color: '#ffffff', flexShrink: 0 }} fontSize="medium" />
                    <div
                        style={{
                            flex: 1,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            color: '#ffffff',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {toast.message}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: 'rgba(255, 255, 255, 0.85)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 6
                        }}
                    >
                        <Close fontSize="small" />
                    </button>
                    {toast.duration > 0 && visible && (
                        <div
                            key={progressKey}
                            style={{
                                position: 'absolute',
                                left: 0,
                                bottom: 0,
                                height: 3,
                                width: '100%',
                                background: 'rgba(255, 255, 255, 0.55)',
                                transformOrigin: 'left center',
                                animation: `toastProgressShrink ${toast.duration}ms linear forwards`
                            }}
                        />
                    )}
                </div>
            )}
        </div>,
        document.body
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
};

export default ToastContext;
